import PurpleRadialGradientBackground from "@/components/background/purple-radial-gradient";
import { PageContainer } from "@/components/container/page-container";
import { SmileIconWithShadow } from "@/components/icons/smile-icon-with-shadow";
import { RoundedModal } from "@/components/modal/rounded-modal";
import { FilledButton } from "@/components/ui/buttons";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { useHandleRecipeProcessingResult } from "@/features/meal-plan/hooks/use-handle-recipe-processing-result";
import { useGetYourFoodStatus } from "@/hooks/requests/your-food/use-get-your-food-status";
import { cn } from "@/utils/cn";
import { textVariants } from "@/utils/text-variants";
import { memo } from "react";
import { useLocation } from "react-router-dom";

const RecipeProcessingPage = memo(function RecipeProcessingPage() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const mealId = searchParams.get("mealId");
  const source = searchParams.get("source");
  const day = searchParams.get("day");
  const targetDate = searchParams.get("targetDate");
  const mealTime = searchParams.get("mealTime");
  const { statusData, isError } = useGetYourFoodStatus({
    mealId,
    enabled: !!mealId,
  });

  const { errorState, router } = useHandleRecipeProcessingResult({
    mealId,
    statusData,
    isError,
    source,
    day,
    targetDate,
    mealTime,
  });

  const getStatusMessage = (status: string) => {
    if (status === "processing") {
      return "Preparing your food...";
    }
    if (status === "completed") {
      return "Food added successfully";
    }
    return "Something went wrong";
  };

  const handleDismissError = () => {
    router.push("/plans/my-plan", "forward", "replace");
  };

  return (
    <PageContainer>
      <PurpleRadialGradientBackground
        classNames={{
          container: cn("w-full !h-fit flex flex-col items-center min-h-screen px-8"),
          image: "bottom-0",
        }}
      >
        <div className="flex flex-1 flex-col items-center pt-4">
          <SmileIconWithShadow />
          <div className="fixed top-1/2 right-0 left-0 flex -translate-y-1/2 flex-col items-center gap-9">
            <div
              className={cn(
                "h-20.5 w-20.5 p-5",
                "flex items-center justify-center",
                "border-gray-theme rounded-4xl border bg-white",
              )}
            >
              <LoadingSpinner size="sm" />
            </div>
            <div className="flex flex-col items-center gap-4">
              <p className={cn(textVariants.bodySingleLine, "text-center")}>
                {getStatusMessage(statusData?.status ?? "processing")}
              </p>
              <p className={cn(textVariants.footnote, "text-center")}>
                {!errorState && "This may take up to 30 seconds."}
              </p>
            </div>
          </div>
        </div>
      </PurpleRadialGradientBackground>

      <RoundedModal isOpen={!!errorState} onDidDismiss={handleDismissError} backdropDismiss>
        <div className="flex flex-col items-center space-y-6 p-6">
          <div className="space-y-4 text-center">
            <h2 className={cn(textVariants.title2)}>{errorState?.title}</h2>
            <p className={cn(textVariants.bodySingleLine, "text-text-gray")}>
              {errorState?.subtitle}
            </p>
          </div>

          <div className="w-full space-y-3">
            <FilledButton text="Go back" color="black" onClick={handleDismissError} />
          </div>
        </div>
      </RoundedModal>
    </PageContainer>
  );
});

export default RecipeProcessingPage;
